/**
 * The dotted guideline grid a card is dropped onto.
 *
 * Guidelines are an aid, never a constraint: a card released close to a line
 * eases onto it, and a card released anywhere else keeps the exact spot it was
 * let go at. That is why the snap is a small distance rather than a modulo of
 * the grid — rounding every drop to the nearest line would quietly refuse the
 * placement the user made.
 *
 * One lattice, one threshold. The grid drawn on the resting canvas, the
 * overlay drawn during a drag, the lines picked out as a box nears them and
 * the correction applied on release all read the two constants below, so the
 * grid someone aims at cannot drift away from the grid the card obeys.
 */

/** Spacing of the dotted guidelines, in flow pixels. */
export const GUIDELINE_SPACING = 40

/**
 * How close to a line a release has to be before it eases onto it.
 *
 * Comfortably under half the spacing: at exactly half every point on the
 * canvas would be within reach of some line and a release between two lines
 * could no longer stay put, which is the whole point of a distance-based snap.
 */
export const GUIDELINE_SNAP_DISTANCE = 18
export const captureDistance = (zoom = 1) => Math.min(GUIDELINE_SNAP_DISTANCE / Math.max(0.01, zoom), GUIDELINE_SPACING * 0.45)

/**
 * The dots are drawn small: the resting grid is a hint the eye can ignore, and
 * the one under a gesture is only a little more present.
 */
export const GUIDELINE_DOT_SIZE = 1
export const GUIDELINE_DRAG_DOT_SIZE = 1.5

/**
 * The `offset` a dotted React Flow background needs for its dots to be painted
 * ON the lattice instead of half a cell away from it.
 *
 * React Flow anchors each dot at the top-left corner of its pattern tile and
 * then shifts the whole pattern by a default that works out to half a cell, so
 * an un-offset grid paints its dots in the MIDDLE of the cells. Same spacing,
 * wrong phase — and a grid in the wrong phase is worse than no grid, because
 * every dot on it is a dot no box will ever settle on. Shifting the pattern by
 * the dot's own radius cancels both and puts every dot on an intersection.
 */
export const guidelineDotOffset = (dotSize: number): number => dotSize / 2

/** The nearest line on one axis, whether or not it is close enough to snap. */
export const nearestGuideline = (value: number): number =>
  Math.round(value / GUIDELINE_SPACING) * GUIDELINE_SPACING

/**
 * The line this axis will settle onto, or null when it stays where it is.
 *
 * The single decision behind both halves of the gesture: what is picked out
 * under a moving box is what the release corrects it to.
 */
export const guidelineFor = (value: number, zoom = 1): number | null => {
  const line = nearestGuideline(value)
  return Math.abs(value - line) <= captureDistance(zoom) ? line : null
}

/** One axis resolved to where it will actually come to rest. */
const snapAxis = (value: number, zoom: number): number => guidelineFor(value, zoom) ?? value

export function snapToGuidelines(position: { x: number; y: number }, zoom = 1): { x: number; y: number } {
  return { x: snapAxis(position.x, zoom), y: snapAxis(position.y, zoom) }
}

/** The lines a box at this position would settle onto — null where none. */
export interface GuidelineIndication {
  spacing?: { from: number; to: number; y: number }[]
  /** The vertical line, at this x in flow coordinates. */
  x: number | null
  /** The horizontal line, at this y in flow coordinates. */
  y: number | null
}

/**
 * What to pick out under a box that is still moving. Each axis is decided on
 * its own, so a box near a vertical line only shows that line, and a box near
 * both shows the intersection it is about to land on.
 */
export function guidelineIndication(position: { x: number; y: number }, zoom = 1): GuidelineIndication {
  return { x: guidelineFor(position.x, zoom), y: guidelineFor(position.y, zoom) }
}

/** Whether an indication has anything to draw at all. */
export const hasIndication = (lines: GuidelineIndication | null): boolean =>
  lines !== null && (lines.x !== null || lines.y !== null || !!lines.spacing?.length)

export interface SnapBox { id: string; x: number; y: number; width: number; height: number; parentId: number | null }
export interface SnapResult {
  position: { x: number; y: number }
  guides: GuidelineIndication
}

/** Object alignment wins over grid; equal sibling spacing wins over alignment.
 * The previous accepted target gets a small retention band, not a new target.
 * Group members receive a single translation; none are independently rounded. */
export function resolveSnap(moving: SnapBox, others: SnapBox[], zoom = 1, previous?: SnapResult | null,
  group: SnapBox[] = [moving]): SnapResult {
  const threshold = captureDistance(zoom)
  const guides: GuidelineIndication = { x: null, y: null }
  const position = { x: moving.x, y: moving.y }
  const candidates: Record<'x' | 'y', { value: number; line: number; priority: number; spacing?: GuidelineIndication['spacing'] }[]> = { x: [], y: [] }
  for (const axis of ['x', 'y'] as const) {
    const size = axis === 'x' ? 'width' : 'height'
    const grid = guidelineFor(moving[axis], zoom)
    if (grid !== null) candidates[axis].push({ value: grid, line: grid, priority: 2 })
    for (const other of [...others].sort((a, b) => a.id.localeCompare(b.id))) {
      for (const fraction of [0, 0.5, 1]) {
        const line = other[axis] + other[size] * fraction
        candidates[axis].push({ value: line - moving[size] * fraction, line, priority: 1 })
      }
    }
  }
  const siblings = others.filter(b => moving.parentId !== null && b.parentId === moving.parentId &&
    Math.abs(b.y - moving.y) <= threshold).sort((a, b) => a.x - b.x || a.id.localeCompare(b.id))
  for (let i = 0; i < siblings.length - 1; i++) {
    const a = siblings[i], b = siblings[i + 1]
    const gap = b.x - a.x - a.width
    const slots = [
      { x: (a.x + a.width + b.x - moving.width) / 2, row: [a, moving, b] },
      { x: b.x + b.width + gap, row: [a, b, moving] },
      { x: a.x - gap - moving.width, row: [moving, a, b] },
    ]
    for (const slot of slots) {
      const row = slot.row.map(n => n.id === moving.id ? { ...n, x: slot.x } : n)
      if (row.some((n, index) => index > 0 && n.x < row[index - 1].x + row[index - 1].width)) continue
      candidates.x.push({ value: slot.x, line: slot.x + moving.width / 2, priority: 0,
        spacing: row.slice(1).map((n, index) => ({ from: row[index].x + row[index].width, to: n.x, y: moving.y - 16 })) })
    }
  }
  for (const axis of ['x', 'y'] as const) {
    const possible = candidates[axis].filter(c => Math.abs(c.value - moving[axis]) <= threshold ||
      (previous?.position[axis] === c.value && Math.abs(c.value - moving[axis]) <= threshold + 3 / Math.max(zoom, 0.01)))
    possible.sort((a, b) => a.priority - b.priority ||
      Number(b.value === previous?.position[axis]) - Number(a.value === previous?.position[axis]) ||
      Math.abs(a.value - moving[axis]) - Math.abs(b.value - moving[axis]) || a.value - b.value)
    const best = possible[0]
    if (best) { position[axis] = best.value; guides[axis] = best.line; if (best.spacing) guides.spacing = best.spacing }
  }
  const dx = position.x - moving.x, dy = position.y - moving.y
  if (group.some(n => others.some(o => n.x + dx < o.x + o.width && n.x + dx + n.width > o.x &&
    n.y + dy < o.y + o.height && n.y + dy + n.height > o.y))) {
    return { position: { x: moving.x, y: moving.y }, guides: { x: null, y: null } }
  }
  return { position, guides }
}
